import type { ColumnDef } from "@tanstack/react-table";
import SegmentedButton, { type SegmentedButtonOption } from "@/components/SegmentedButton/SegmentedButton";

export interface BuildSegmentedButtonColumnProps<T> {
  id: string;
  header: string;
  options: SegmentedButtonOption[];
  getValue: (row: T) => string | null;
  onChange: (row: T, value: string | null) => void;
  disabled?: boolean | ((row: T) => boolean);
  allowDeselect?: boolean;
  showCheckmark?: boolean;
  compactLabel?: (row: T) => string;
  size?: number;
  enableSorting?: boolean;
  meta?: Record<string, unknown>;
}

/**
 * Creates a column that renders a SegmentedButton on desktop and a read-only
 * label on compact layouts. On compact, wire `onEditClick` in `buildActionColumn`
 * to open a SideSheetModal containing the SegmentedButton for the selected row.
 *
 * @example
 * ```tsx
 * buildSegmentedButtonColumn<SessionRegistrationRow>({
 *   id: "attendance",
 *   header: "Attendance",
 *   options: [
 *     { label: "Present", value: "true" },
 *     { label: "Absent", value: "false" }
 *   ],
 *   getValue: (row) => row.attended === null ? null : String(row.attended),
 *   onChange: (row, value) => onAttendanceUpdate(row.id, value),
 *   disabled: !canEdit,
 *   compactLabel: (row) =>
 *     row.attended === true ? "Present" : row.attended === false ? "Absent" : "--"
 * })
 * ```
 */
export function buildSegmentedButtonColumn<T>({
  id,
  header,
  options,
  getValue,
  onChange,
  disabled = false,
  allowDeselect = true,
  showCheckmark = true,
  compactLabel,
  size,
  enableSorting = false,
  meta
}: BuildSegmentedButtonColumnProps<T>): ColumnDef<T, unknown> {
  return {
    id,
    header,
    enableSorting,
    ...(size && { size }),
    cell: (cellProps) => {
      const { row, table } = cellProps;
      const isCompact = table.options.meta?.isCompactLayout;
      const currentValue = getValue(row.original);
      const isDisabled = typeof disabled === "function" ? disabled(row.original) : disabled;

      if (isCompact) {
        const label = compactLabel?.(row.original) ?? options.find((o) => o.value === currentValue)?.label ?? "--";
        return <span className="text-body-small">{label}</span>;
      }

      return (
        <SegmentedButton
          key={`${id}-${row.id}`}
          options={options}
          value={currentValue}
          onChange={(value) => onChange(row.original, value)}
          showCheckmark={showCheckmark}
          allowDeselect={allowDeselect}
          disabled={isDisabled}
        />
      );
    },
    ...(meta && { meta })
  };
}
